import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator,
    faCartShopping,
    faCopy,
    faDownload,
    faFloppyDisk,
    faFolderOpen,
    faListCheck,
    faUpload,
    faRotateLeft,
    faRightToBracket,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import {
    CalculateResponse,
    LeistungenResponse,
    OfferFragments,
    RequirementGroup,
    RequirementItem,
    Rubric,
    useCalculatorApi,
} from './calculatorApi';
import {
    collectProject,
    configuredRequirementIds,
    Instance,
    InstanceMap,
    newInstance,
    projectToInstances,
} from './instanceModel';
import { RequirementInputTree } from './RequirementInputTree';
import { Warenkorb } from './Warenkorb';
import { LoadProjectDialog, SaveProjectDialog, SaveProjectValues } from './ProjectStore';
import { SaveProjectRequest, StoredProject } from 'apiClient/Api';

const money = (v: number) =>
    Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const indexTree = (group: RequirementGroup, into: Map<string, RequirementItem>) => {
    for (const i of group.items) into.set(i.id, i);
    for (const s of group.groups) indexTree(s, into);
};

const emptyInstances = (index: Map<string, RequirementItem>): InstanceMap => {
    const map: InstanceMap = {};
    index.forEach((_item, id) => {
        map[id] = [newInstance()];
    });
    return map;
};

// Ergebnis-Tabs.
const CART_TAB = 0;
const FRAGMENT_TAB = 1;
const LV_TAB = 2;
const LOG_TAB = 3;

const stripTags = (html: string) => html.replace(/<[^>]+>/g, '');

// Fallback für Kontexte ohne Clipboard-API (z. B. App über HTTP/IP statt HTTPS/localhost).
const execCommandCopyText = (text: string): boolean => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch {
        ok = false;
    }
    document.body.removeChild(ta);
    return ok;
};

const copyPlainText = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fällt auf execCommand zurück */
    }
    return execCommandCopyText(text);
};

const HtmlFragment: React.FC<{ html: string }> = ({ html }) => (
    <Box
        sx={{
            '& table': { borderCollapse: 'collapse', width: '100%', mt: 2 },
            '& th, & td': { border: (t) => `1px solid ${t.palette.divider}`, p: 0.75, textAlign: 'left' },
            '& th': { bgcolor: 'action.hover' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

export const Calculation: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle, apiError, api } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const calc = useCalculatorApi();
    const fileInput = useRef<HTMLInputElement>(null);
    // Verstecktes Element für den Rich-Text-Fallback (execCommand auf einer DOM-Auswahl).
    const clipRef = useRef<HTMLDivElement>(null);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [tree, setTree] = useState<RequirementGroup | null>(null);
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [index, setIndex] = useState<Map<string, RequirementItem>>(new Map());
    const [instances, setInstances] = useState<InstanceMap>({});
    const [projektname, setProjektname] = useState('');
    const [reqOrder, setReqOrder] = useState<'xml' | 'name'>(
        (localStorage.getItem('ok-req-order') as 'xml' | 'name') || 'xml',
    );

    const [response, setResponse] = useState<CalculateResponse | null>(null);
    const [resultTab, setResultTab] = useState(0);
    // Es ist immer nur eine Ansicht sichtbar: Anforderungen ODER Kalkulation/Warenkorb.
    const [view, setView] = useState<'input' | 'result'>('input');

    // Leistungsmerkmale (lazy: erst beim Öffnen des Tabs geladen)
    const [leistungen, setLeistungen] = useState<LeistungenResponse | null>(null);
    const [leistungenStale, setLeistungenStale] = useState(true);
    const [leistungenLoading, setLeistungenLoading] = useState(false);
    const [lvSelected, setLvSelected] = useState<Set<string>>(new Set());
    const [lvMuss, setLvMuss] = useState(false);
    const lastProjectRef = useRef<ReturnType<typeof collectProject> | null>(null);

    const [copyTable, setCopyTable] = useState(false);
    const [labelwinEnabled, setLabelwinEnabled] = useState(false);

    // Projektspeicher (DB)
    const [storedId, setStoredId] = useState<string | null>(null);
    const [storedName, setStoredName] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerReference, setCustomerReference] = useState('');
    const [supportContractId, setSupportContractId] = useState<string | null>(null);
    const [saveOpen, setSaveOpen] = useState(false);
    const [loadOpen, setLoadOpen] = useState(false);
    const calculatedAtRef = useRef<string | null>(null);

    useEffect(() => setTitle(t('calculation.title')), [setTitle, t]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [rubricList, requirementTree] = await Promise.all([
                    calc.listRubrics().catch(() => [] as Rubric[]),
                    calc.getRequirements(),
                ]);
                if (!active) return;
                const idx = new Map<string, RequirementItem>();
                indexTree(requirementTree, idx);
                setRubrics(rubricList);
                setTree(requirementTree);
                setIndex(idx);
                setInstances(emptyInstances(idx));
                calc.labelwinStatus()
                    .then((s) => active && setLabelwinEnabled(!!s.enabled))
                    .catch(() => undefined);
            } catch (e) {
                apiError(e);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const configured = useMemo(() => configuredRequirementIds(instances), [instances]);

    // ---- Instanz-Mutatoren ----
    const addInstance = useCallback((reqId: string) => {
        setInstances((prev) => ({ ...prev, [reqId]: [...(prev[reqId] || []), newInstance()] }));
    }, []);

    const removeInstance = useCallback((reqId: string, iid: number) => {
        setInstances((prev) => {
            const next = (prev[reqId] || []).filter((i) => i.iid !== iid);
            return { ...prev, [reqId]: next.length ? next : [newInstance()] };
        });
    }, []);

    const updateInstance = useCallback((reqId: string, iid: number, patch: Partial<Instance>) => {
        setInstances((prev) => ({
            ...prev,
            [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, ...patch } : i)),
        }));
    }, []);

    const updateOpt = useCallback(
        (reqId: string, iid: number, fn: (prev: Record<string, number>) => Record<string, number>) => {
            setInstances((prev) => ({
                ...prev,
                [reqId]: (prev[reqId] || []).map((i) => (i.iid === iid ? { ...i, opt: fn(i.opt) } : i)),
            }));
        },
        [],
    );

    const changeReqOrder = (value: 'xml' | 'name') => {
        setReqOrder(value);
        localStorage.setItem('ok-req-order', value);
    };

    // ---- Aktionen ----
    const calculate = useCallback(async () => {
        const project = collectProject(projektname, instances, index);
        if ((project.instanzen?.length ?? 0) === 0) {
            enqueueSnackbar(t('calculation.noInput'), { variant: 'warning' });
            return;
        }
        setBusy(true);
        try {
            const res = await calc.calculateProject({ project });
            setResponse(res);
            lastProjectRef.current = project;
            calculatedAtRef.current = new Date().toISOString();
            setLeistungen(null);
            setLeistungenStale(true);
            setResultTab(0);
            setView('result');
        } catch (e) {
            apiError(e);
        } finally {
            setBusy(false);
        }
    }, [projektname, instances, index, calc, enqueueSnackbar, t, apiError]);

    const fetchLeistungen = useCallback(
        async (resetDomains: boolean) => {
            const project = lastProjectRef.current;
            if (!project) return;
            const domains = !resetDomains && lvSelected.size ? Array.from(lvSelected) : null;
            setLeistungenLoading(true);
            try {
                const res = await calc.generateLeistungen({ project, domains });
                setLeistungen(res);
                setLeistungenStale(false);
                if (resetDomains) setLvSelected(new Set(res.availableDomains || []));
            } catch (e) {
                apiError(e);
            } finally {
                setLeistungenLoading(false);
            }
        },
        [calc, lvSelected, apiError],
    );

    // Leistungsmerkmale beim Öffnen des Tabs (Index 2) laden, sofern noch nicht geschehen.
    useEffect(() => {
        if (resultTab === LV_TAB && response && leistungenStale && !leistungenLoading) {
            fetchLeistungen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultTab, response, leistungenStale]);

    const onResultTabChange = (_: React.SyntheticEvent, value: number) => setResultTab(value);

    const toggleDomain = (domain: string, checked: boolean) => {
        const next = new Set(lvSelected);
        if (checked) next.add(domain);
        else next.delete(domain);
        setLvSelected(next);
        const project = lastProjectRef.current;
        if (project) {
            calc.generateLeistungen({ project, domains: next.size ? Array.from(next) : null })
                .then((res) => {
                    setLeistungen(res);
                    setLeistungenStale(false);
                })
                .catch(apiError);
        }
    };

    const reset = () => {
        setInstances(emptyInstances(index));
        setResponse(null);
        setLeistungen(null);
        setLeistungenStale(true);
        lastProjectRef.current = null;
        setView('input');
        setStoredId(null);
        setStoredName('');
        setCustomerName('');
        setCustomerReference('');
        setSupportContractId(null);
    };

    // ---- Projektspeicher (DB) ----
    const buildSaveBody = (name: string, values?: SaveProjectValues): SaveProjectRequest => ({
        id: storedId,
        name,
        project: collectProject(projektname, instances, index),
        customerReference: (values?.customerReference ?? customerReference) || null,
        customerName: (values?.customerName ?? customerName) || null,
        supportContractId: (values?.supportContractId ?? supportContractId) || null,
        lastCalculation: response
            ? {
                  totalNet: response.result.summary.gesamt,
                  positionCount: response.result.positions.filter((p) => p.kind === 'Visible' || p.kind === 'Hidden')
                      .length,
                  calculatedAt: calculatedAtRef.current ?? new Date().toISOString(),
              }
            : null,
    });

    const applyStored = (s: StoredProject) => {
        setStoredId(s.id ?? null);
        setStoredName(s.name ?? '');
        setCustomerName(s.customerName ?? '');
        setCustomerReference(s.customerReference ?? '');
        setSupportContractId(s.supportContractId ?? null);
    };

    // „Speichern": überschreibt das geladene Projekt (PUT) oder öffnet den Dialog für eine Neuanlage.
    const handleSave = async () => {
        if (!storedId) {
            setSaveOpen(true);
            return;
        }
        try {
            const r = await api.calculator.projectsUpdate(storedId, buildSaveBody(storedName));
            applyStored(r.data as unknown as StoredProject);
            enqueueSnackbar(t('calculation.store.saved'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    // „Speichern unter": legt immer neu an (POST).
    const handleSaveSubmit = async (values: SaveProjectValues) => {
        try {
            const body = { ...buildSaveBody(values.name, values), id: null };
            const r = await api.calculator.projectsCreate(body);
            applyStored(r.data as unknown as StoredProject);
            setProjektname((p) => p || values.name);
            enqueueSnackbar(t('calculation.store.saved'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    const handleLoadProject = async (id: string) => {
        try {
            const r = await api.calculator.projectsGet(id);
            const s = r.data as unknown as StoredProject;
            const project = s.project ?? { projektname: '', instanzen: [] };
            setProjektname(project.projektname || s.name || '');
            setInstances({ ...emptyInstances(index), ...projectToInstances(project) });
            applyStored(s);
            setResponse(null);
            setLeistungen(null);
            setLeistungenStale(true);
            lastProjectRef.current = null;
            setView('input');
            enqueueSnackbar(t('calculation.projectLoaded'), { variant: 'success' });
        } catch (e) {
            apiError(e);
        }
    };

    const saveProject = () => {
        const project = collectProject(projektname, instances, index);
        const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        a.download = `${(project.projektname || 'projekt').replace(/[^\w.-]+/g, '_')}_${stamp}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const loadProjectFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const project = JSON.parse(String(reader.result));
                setProjektname(project.projektname || '');
                setInstances({ ...emptyInstances(index), ...projectToInstances(project) });
                enqueueSnackbar(t('calculation.projectLoaded'), { variant: 'success' });
            } catch {
                enqueueSnackbar(t('calculation.invalidProject'), { variant: 'error' });
            }
        };
        reader.readAsText(file);
    };

    // Rich-Text-Kopie: bevorzugt Clipboard-API (HTML), sonst Auswahl eines gerenderten Knotens
    // + execCommand (funktioniert auch ohne sicheren Kontext, z. B. App über HTTP/IP).
    const copyRich = async (html: string): Promise<boolean> => {
        try {
            if (navigator.clipboard?.write && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([stripTags(html)], { type: 'text/plain' }),
                    }),
                ]);
                return true;
            }
        } catch {
            /* fällt auf die DOM-Auswahl zurück */
        }
        const node = clipRef.current;
        if (node) {
            node.innerHTML = html;
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(node);
            selection?.removeAllRanges();
            selection?.addRange(range);
            let ok = false;
            try {
                ok = document.execCommand('copy');
            } catch {
                ok = false;
            }
            selection?.removeAllRanges();
            node.innerHTML = '';
            if (ok) return true;
        }
        return execCommandCopyText(stripTags(html));
    };

    const copyFragment = async (kind: 'html' | 'markdown' | 'lvHtml' | 'lvMd') => {
        const fragments: OfferFragments | undefined = response?.fragments;
        let ok = false;
        if (kind === 'html' && fragments) {
            ok = await copyRich(fragments.html + (copyTable ? fragments.tableHtml : ''));
        } else if (kind === 'markdown' && fragments) {
            ok = await copyPlainText(fragments.markdown + (copyTable ? '\n' + fragments.tableMarkdown : ''));
        } else if (kind === 'lvHtml' && leistungen) {
            ok = await copyRich((lvMuss ? leistungen.lv : leistungen.leistungsangabe).html);
        } else if (kind === 'lvMd' && leistungen) {
            ok = await copyPlainText((lvMuss ? leistungen.lv : leistungen.leistungsangabe).markdown);
        }
        enqueueSnackbar(t(ok ? 'calculation.copied' : 'calculation.copyFailed'), {
            variant: ok ? 'success' : 'error',
        });
    };

    const exportToLabelWin = async () => {
        const project = collectProject(projektname, instances, index);
        if ((project.instanzen?.length ?? 0) === 0) {
            enqueueSnackbar(t('calculation.noInput'), { variant: 'warning' });
            return;
        }
        setBusy(true);
        try {
            const r = await calc.labelwinExport({ project });
            enqueueSnackbar(t('calculation.labelwin.exported', { count: r.written }), { variant: 'success' });
        } catch (e) {
            // 400 = Connector inaktiv ODER kein LabelBenutzer hinterlegt → Server-Meldung bevorzugen.
            const resp = (e as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
            if (resp?.status === 400) {
                enqueueSnackbar(resp.data?.error || resp.data?.message || t('calculation.labelwin.missingUser'), {
                    variant: 'warning',
                });
            } else {
                apiError(e);
            }
        } finally {
            setBusy(false);
        }
    };

    const summary = response?.result.summary;
    const warnings = response?.result.warnings || [];
    const activeLv = leistungen ? (lvMuss ? leistungen.lv : leistungen.leistungsangabe) : null;

    return (
        <Grid size={12}>
            <Title>{t('calculation.title')}</Title>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label={t('calculation.projectName')}
                        size="small"
                        value={projektname}
                        onChange={(e) => setProjektname(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faCalculator} />}
                        onClick={calculate}
                        disabled={busy || loading}
                    >
                        {t('calculation.calculate')}
                    </Button>
                    <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faFloppyDisk} />} onClick={handleSave}>
                        {t('calculation.store.save')}
                    </Button>
                    <Button startIcon={<FontAwesomeIcon icon={faCopy} />} onClick={() => setSaveOpen(true)}>
                        {t('calculation.store.saveAs')}
                    </Button>
                    <Button startIcon={<FontAwesomeIcon icon={faFolderOpen} />} onClick={() => setLoadOpen(true)}>
                        {t('calculation.store.load')}
                    </Button>
                    <Button startIcon={<FontAwesomeIcon icon={faDownload} />} onClick={saveProject}>
                        {t('calculation.export')}
                    </Button>
                    <Button startIcon={<FontAwesomeIcon icon={faUpload} />} onClick={() => fileInput.current?.click()}>
                        {t('calculation.import')}
                    </Button>
                    <Button startIcon={<FontAwesomeIcon icon={faRotateLeft} />} color="inherit" onClick={reset}>
                        {t('calculation.reset')}
                    </Button>
                    {storedName && (
                        <Chip
                            color="info"
                            variant="outlined"
                            icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                            label={t('calculation.store.loadedAs', { name: storedName })}
                        />
                    )}
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faRightToBracket} />}
                        onClick={exportToLabelWin}
                        disabled={busy}
                    >
                        {t('calculation.labelwin.export')}
                    </Button>
                    {!labelwinEnabled && (
                        <Chip size="small" variant="outlined" color="warning" label={t('calculation.labelwin.inactive')} />
                    )}
                    <input
                        ref={fileInput}
                        type="file"
                        accept="application/json,.json"
                        hidden
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) loadProjectFile(f);
                            e.target.value = '';
                        }}
                    />
                </Stack>
            </Paper>

            <ToggleButtonGroup
                value={view}
                exclusive
                color="primary"
                size="small"
                onChange={(_, v) => v && setView(v)}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="input">
                    <FontAwesomeIcon icon={faListCheck} style={{ marginRight: 8 }} />
                    {t('calculation.input')}
                </ToggleButton>
                <ToggleButton value="result">
                    <FontAwesomeIcon icon={faCartShopping} style={{ marginRight: 8 }} />
                    {t('calculation.tabs.cart')}
                </ToggleButton>
            </ToggleButtonGroup>

            {(loading || busy) && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={2}>
                {/* Eingabe */}
                {view === 'input' && (
                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">{t('calculation.input')}</Typography>
                            <TextField
                                select
                                size="small"
                                label={t('calculation.order')}
                                value={reqOrder}
                                onChange={(e) => changeReqOrder(e.target.value as 'xml' | 'name')}
                            >
                                <MenuItem value="xml">{t('calculation.orderXml')}</MenuItem>
                                <MenuItem value="name">{t('calculation.orderName')}</MenuItem>
                            </TextField>
                        </Stack>
                        {tree ? (
                            <RequirementInputTree
                                tree={tree}
                                rubrics={rubrics}
                                instances={instances}
                                configured={configured}
                                reqOrder={reqOrder}
                                addInstance={addInstance}
                                removeInstance={removeInstance}
                                updateInstance={updateInstance}
                                updateOpt={updateOpt}
                            />
                        ) : (
                            !loading && <Typography color="text.secondary">{t('calculation.noData')}</Typography>
                        )}
                    </Paper>
                </Grid>
                )}

                {/* Ergebnis */}
                {view === 'result' && (
                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        {summary && (
                            <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, flexWrap: 'wrap' }}>
                                <Chip label={`${t('calculation.summary.base')}: ${money(summary.basisSystem)}`} />
                                {summary.softwareLoesung > 0 && (
                                    <Chip label={`${t('calculation.summary.software')}: ${money(summary.softwareLoesung)}`} />
                                )}
                                <Chip label={`${t('calculation.summary.units')}: ${money(summary.bedieneinheiten)}`} />
                                <Chip label={`${t('calculation.summary.service')}: ${money(summary.wartung)}`} />
                                <Chip color="primary" label={`${t('calculation.summary.total')}: ${money(summary.gesamt)}`} />
                            </Stack>
                        )}

                        {warnings.map((w, i) => (
                            <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                                {w}
                            </Alert>
                        ))}

                        <Tabs value={resultTab} onChange={onResultTabChange} variant="scrollable" sx={{ mb: 2 }}>
                            <Tab icon={<FontAwesomeIcon icon={faCartShopping} />} iconPosition="start" label={t('calculation.tabs.cart')} />
                            <Tab label={t('calculation.tabs.fragment')} />
                            <Tab label={t('calculation.tabs.features')} />
                            <Tab label={t('calculation.tabs.log')} />
                        </Tabs>

                        {!response && <Typography color="text.secondary">{t('calculation.runHint')}</Typography>}

                        {response && (resultTab === CART_TAB || resultTab === FRAGMENT_TAB) && (
                            <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FontAwesomeIcon icon={faCopy} />}
                                    onClick={() => copyFragment('html')}
                                >
                                    {t('calculation.copyFormatted')}
                                </Button>
                                <Button size="small" onClick={() => copyFragment('markdown')}>
                                    {t('calculation.copyMarkdown')}
                                </Button>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={copyTable} onChange={(e) => setCopyTable(e.target.checked)} />}
                                    label={t('calculation.copyTable')}
                                />
                            </Stack>
                        )}

                        {response && resultTab === CART_TAB && (
                            <Warenkorb positions={response.result.positions} summary={response.result.summary} />
                        )}

                        {response && resultTab === FRAGMENT_TAB && (
                            <HtmlFragment html={(response.fragments.html || '') + (response.fragments.tableHtml || '')} />
                        )}

                        {response && resultTab === LV_TAB && (
                            <>
                                <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={lvMuss} onChange={(e) => setLvMuss(e.target.checked)} />}
                                        label={t('calculation.lvMuss')}
                                    />
                                    <Button size="small" startIcon={<FontAwesomeIcon icon={faCopy} />} onClick={() => copyFragment('lvHtml')}>
                                        {t('calculation.copyFormatted')}
                                    </Button>
                                    <Button size="small" onClick={() => copyFragment('lvMd')}>
                                        {t('calculation.copyMarkdown')}
                                    </Button>
                                </Stack>
                                {leistungen && leistungen.availableDomains.length > 0 && (
                                    <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
                                        {leistungen.availableDomains.map((d) => (
                                            <FormControlLabel
                                                key={d}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={lvSelected.has(d)}
                                                        onChange={(e) => toggleDomain(d, e.target.checked)}
                                                    />
                                                }
                                                label={d}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                <Divider sx={{ mb: 1 }} />
                                {leistungenLoading ? (
                                    <LinearProgress />
                                ) : activeLv && activeLv.html ? (
                                    <HtmlFragment html={activeLv.html} />
                                ) : (
                                    <Typography color="text.secondary">{t('calculation.noFeatures')}</Typography>
                                )}
                            </>
                        )}

                        {response && resultTab === LOG_TAB && (
                            <Box
                                component="pre"
                                sx={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'monospace', m: 0 }}
                            >
                                {(response.result.log || []).map((l) => `[${l.level}] ${l.message}`).join('\n')}
                            </Box>
                        )}
                    </Paper>
                </Grid>
                )}
            </Grid>

            <SaveProjectDialog
                open={saveOpen}
                title={t('calculation.store.saveAs')}
                initial={{
                    name: storedName || projektname || '',
                    customerReference,
                    customerName,
                    supportContractId,
                }}
                onClose={() => setSaveOpen(false)}
                onSubmit={handleSaveSubmit}
            />
            <LoadProjectDialog open={loadOpen} onClose={() => setLoadOpen(false)} onLoad={handleLoadProject} />

            {/* Verstecktes Ziel für den Rich-Text-Kopier-Fallback. */}
            <Box
                ref={clipRef}
                aria-hidden
                sx={{ position: 'fixed', left: -99999, top: 0, width: 1, height: 1, overflow: 'hidden' }}
            />
        </Grid>
    );
};

export default Calculation;
